// utilities.js (modular exports)
import { ref, computed, watch } from 'vue'
import { isEqual } from 'lodash' // eslint-disable-line

export const computeSpaceBehindWindowOf = el => {
  const { left, top } = el.getBoundingClientRect()
  return {
    width: Math.abs(Math.min(left, 0)),
    height: Math.abs(Math.min(top, 0))
  }
}

export const getGridMeasurement = rootEl => {
  const computedStyle = window.getComputedStyle(rootEl)
  return {
    rowGap: parseInt(computedStyle.getPropertyValue('row-gap')) || 0,
    colGap: parseInt(computedStyle.getPropertyValue('column-gap')) || 0,
    flow: computedStyle.getPropertyValue('grid-auto-flow').startsWith('column') ? 'column' : 'row',
    columns: computedStyle.getPropertyValue('grid-template-columns').split(' ').length,
    rows: computedStyle.getPropertyValue('grid-template-rows').split(' ').length
  }
}

export const getResizeMeasurement = (rootEl, { height, width }) => {
  const grid = getGridMeasurement(rootEl)
  return {
    ...grid,
    itemHeightWithGap: height + grid.rowGap,
    itemWidthWithGap: width + grid.colGap
  }
}

export const useReactiveBuffer = ({ pages, nbHits, perPage, resizeMeasurement, spaceBehindWindow, bufferRatio, onRevealPages }) => {
  const bufferMeta = computed(() => {
    const { flow, columns, rows, itemWidthWithGap, itemHeightWithGap, colGap, rowGap } = resizeMeasurement.value
    const isRow = flow === 'row'
    const cross = isRow ? columns : rows
    const gap = isRow ? rowGap : colGap
    const size = isRow ? itemHeightWithGap : itemWidthWithGap
    const win = isRow ? window.innerHeight : window.innerWidth
    const behind = isRow ? spaceBehindWindow.value.height : spaceBehindWindow.value.width
    const lines = Math.ceil((win + gap) / size)
    const offset = Math.floor((behind + gap) / size) * cross
    // const bufferedOffset = Math.max(offset - Math.floor(lines * cross * bufferRatio.value / 2), 0)
    // const bufferedLength = Math.ceil(lines * cross * bufferRatio.value)
    const bufferAhead = Math.floor(lines * cross * (bufferRatio.value - 1) / 2)
    const bufferedOffset = Math.max(offset - bufferAhead, 0)
    const bufferedLength = lines * cross * bufferRatio.value
    return { bufferedOffset, bufferedLength }
  })

  const buffer = ref([])

  watch(
    [bufferMeta, resizeMeasurement, pages, nbHits, perPage],
    (
      [bufferMetaNew, resizeMeasurementNew, pagesNew, nbHitsNew, perPageNew],
      [bufferMetaOld, resizeMeasurementOld, pagesOld, nbHitsOld, perPageOld]
    ) => {
      const { bufferedOffset, bufferedLength } = bufferMeta.value
      const next = []
      const pageSet = new Set()
      // console.log('%cbuffer:', 'color:magenta', pagesNew)

      for (let i = bufferedOffset; i < bufferedOffset + bufferedLength && i < nbHitsNew; i++) {
        const page = Math.floor(i / perPageNew)
        const local = i % perPageNew
        pageSet.add(page)
        const val = pagesNew.get(page)?.[local]
        const { x, y } = getItemOffsetByIndex(i, resizeMeasurementNew)
        next.push({
          index: i,
          value: val,
          offset: { x, y },
          style: { gridArea: '1/1', transform: `translate(${x}px, ${y}px)` }
        })
      }

      if (
        typeof onRevealPages === 'function'
        && !isEqual(bufferMetaNew, bufferMetaOld)
        && !isEqual(bufferMetaNew, bufferMetaOld)
        // && !isEqual(pagesNew, pagesOld)
      ) {
        onRevealPages(pageSet)
        // console.log({
        //   bufferMeta: isEqual(bufferMetaNew, bufferMetaOld),
        //   resizeMeasurement: isEqual(resizeMeasurementNew, resizeMeasurementOld),
        //   pages: isEqual(pagesNew, pagesOld)
        // })
      }
      // onRevealPages(pageSet)

      const prevMap = new Map(buffer.value.map(item => [item.index, item]))
      buffer.value = next.map(item => {
        const existing = prevMap.get(item.index)
        if (existing && existing.value === item.value) {
          existing.style = item.style
          return existing
        }
        return item
      })
    }, { immediate: true })

  const contentSize = computed(() => {
    const { flow, columns, rows, itemWidthWithGap, itemHeightWithGap, colGap, rowGap } = resizeMeasurement.value
    return flow === 'row'
      ? { height: itemHeightWithGap * Math.ceil(nbHits.value / columns) - rowGap }
      : { width: itemWidthWithGap * Math.ceil(nbHits.value / rows) - colGap }
  })

  return { buffer, contentSize }
}

export const getScrollParents = (el, includeHidden = false) => {
  const style = getComputedStyle(el)
  if (style.position === 'fixed') {
    return { vertical: document.body, horizontal: document.body }
  }

  const overflowRegex = includeHidden ? /(auto|scroll|hidden)/ : /(auto|scroll)/
  const excludeStaticParent = style.position === 'absolute'

  let vertical, horizontal

  for (let parent = el; (parent = parent.parentElement);) {
    const style = getComputedStyle(parent)
    if (excludeStaticParent && style.position === 'static') continue
    if (!horizontal && overflowRegex.test(style.overflowX)) horizontal = parent
    if (!vertical && overflowRegex.test(style.overflowY)) vertical = parent
    if (horizontal && vertical) break
  }

  const fallback = document.scrollingElement || document.documentElement
  return {
    vertical: vertical || fallback,
    horizontal: horizontal || fallback
  }
}

export const getItemOffsetByIndex = (index, { flow, columns, rows, itemWidthWithGap, itemHeightWithGap }) => {
  return flow === 'row'
    ? { x: (index % columns) * itemWidthWithGap, y: Math.floor(index / columns) * itemHeightWithGap }
    : { x: Math.floor(index / rows) * itemWidthWithGap, y: (index % rows) * itemHeightWithGap }
}
